import { connectRouter, RouterAction } from 'connected-react-router';
import { History } from 'history';
import { combineReducers } from 'redux';
import { createMigrate, persistReducer } from 'redux-persist';
import { Storage } from 'redux-persist/es/types';
import { all } from 'redux-saga/effects';
import { RootActionType } from '../utils/reduxUtils';
import { appActions, appBlacklist, appMigrations, appReducer, appSagas } from '../pages/app';
import { appFeedbackActions, appFeedbackReducer, appFeedbackSagas } from '../pages/appFeedback';
import { commonActions, commonReducer, commonSagas } from '../pages/common';
import { navigationActions, navigationReducer, navigationSagas } from '../pages/common/navigation';
import {
  createTourActions,
  createTourMigrations,
  createTourReducer,
  createTourSagas,
  createTourWhitelist,
} from '../pages/createTour';
import { findTourActions, findTourReducer, findTourSagas, findTourWhitelist } from '../pages/findTour';
import {
  followAuthorActions,
  followAuthorReducer,
  followAuthorSagas,
  followAuthorWhitelist,
} from '../pages/followAuthor';
import { landingPageActions, landingPageReducer, landingPageSagas } from '../pages/landing';
import { IMyToursState, myToursActions, myToursReducer, myToursSagas } from '../pages/myTours';
import {
  notificationActions,
  notificationReducer,
  notificationSagas,
  notificationWhitelist,
} from '../pages/notifications';
import {
  playTourActions,
  playTourMigrations,
  playTourReducer,
  playTourSagas,
  playTourWhitelist,
} from '../pages/playTour';
import { profileActions, profileReducer, profileSagas } from '../pages/profile';
import { publicProfileActions, publicProfileReducer, publicProfileSagas } from '../pages/publicProfile';
import { tourContentActions, tourContentReducer, tourContentSagas } from '../pages/tourContent';
import { tourDetailActions, tourDetailReducer, tourDetailSagas } from '../pages/tourDetail';
import {
  tourStopOfflineStatusActions,
  tourStopOfflineStatusReducer,
  tourStopOfflineStatusSagas,
} from '../pages/tourStopOfflineStatus';
import { dateTransform } from './transformers';
import { StateType } from '../utils/reducerBuilder';

function createMigrateAndVersion(migrations: any) {
  const persistVersions = Object.keys(migrations).map(m => parseInt(m));
  return {
    version: Math.max(...persistVersions),
    migrate: createMigrate(migrations),
  };
}

function createRootReducer(history: History, storage: Storage) {
  const appPersistConfig = {
    key: 'app',
    blacklist: appBlacklist,
    transforms: [dateTransform],
    storage,
    ...createMigrateAndVersion(appMigrations),
  };

  const createTourPersistConfig = {
    key: 'createTour',
    whitelist: createTourWhitelist,
    transforms: [dateTransform],
    storage,
    ...createMigrateAndVersion(createTourMigrations),
  };

  const findTourPersistConfig = {
    key: 'findTour',
    whitelist: findTourWhitelist,
    transforms: [dateTransform],
    storage,
  };

  const playTourPersistConfig = {
    key: 'playTour',
    whitelist: playTourWhitelist,
    transforms: [dateTransform],
    storage,
    ...createMigrateAndVersion(playTourMigrations),
  };

  const myToursWhitelist: (keyof IMyToursState)[] = ['playedTours', 'createdTours'];
  const myToursPersistConfig = {
    key: 'myTours',
    whitelist: myToursWhitelist,
    transforms: [dateTransform],
    storage,
  };

  const followAuthorPersistConfig = {
    key: 'followAuthor',
    whitelist: followAuthorWhitelist,
    transforms: [dateTransform],
    storage,
  };

  const notificationPersistConfig = {
    key: 'notification',
    whitelist: notificationWhitelist,
    transforms: [dateTransform],
    storage,
  };

  const combinedReducer = combineReducers({
    router: connectRouter(history),
    app: persistReducer(appPersistConfig, appReducer),
    previousLocation: navigationReducer,
    createTour: persistReducer(createTourPersistConfig, createTourReducer),
    findTour: persistReducer(findTourPersistConfig, findTourReducer),
    playTour: persistReducer(playTourPersistConfig, playTourReducer),
    myTours: persistReducer(myToursPersistConfig, myToursReducer),
    followAuthor: persistReducer(followAuthorPersistConfig, followAuthorReducer),
    notification: persistReducer(notificationPersistConfig, notificationReducer),
    common: commonReducer,
    tourDetail: tourDetailReducer,
    profile: profileReducer,
    publicProfile: publicProfileReducer,
    appFeedback: appFeedbackReducer,
    tourContent: tourContentReducer,
    landingPage: landingPageReducer,
    tourStopOfflineStatus: tourStopOfflineStatusReducer,
  });

  return combinedReducer;
}

export function createPersistedReducer(history: History<any>, storage: Storage) {
  const rootReducer = createRootReducer(history, storage);

  const persistConfig = {
    key: 'root',
    storage,
    blacklist: [
      'router',
      'previousLocation',
      'app',
      'createTour',
      'findTour',
      'playTour',
      'myTours',
      'followAuthor',
      'notification',
      'common',
      'profile',
      'publicProfile',
      'tourContent',
      'appFeedback',
      'tourDetail',
      'landingPage',
    ],
  };

  return persistReducer(persistConfig, rootReducer);
}

export function* rootSaga() {
  yield all([
    ...navigationSagas,
    ...createTourSagas,
    ...findTourSagas,
    ...myToursSagas,
    ...playTourSagas,
    ...tourDetailSagas,
    ...profileSagas,
    ...publicProfileSagas,
    ...tourContentSagas,
    ...appFeedbackSagas,
    ...commonSagas,
    ...appSagas,
    ...landingPageSagas,
    ...followAuthorSagas,
    ...notificationSagas,
    ...tourStopOfflineStatusSagas,
  ]);
}

export type RootState = StateType<ReturnType<typeof createRootReducer>>;

export type RootAction =
  | RootActionType<typeof navigationActions>
  | RootActionType<typeof createTourActions>
  | RootActionType<typeof findTourActions>
  | RootActionType<typeof playTourActions>
  | RootActionType<typeof myToursActions>
  | RootActionType<typeof tourDetailActions>
  | RootActionType<typeof profileActions>
  | RootActionType<typeof publicProfileActions>
  | RootActionType<typeof commonActions>
  | RootActionType<typeof appActions>
  | RootActionType<typeof appFeedbackActions>
  | RootActionType<typeof landingPageActions>
  | RootActionType<typeof followAuthorActions>
  | RootActionType<typeof notificationActions>
  | RootActionType<typeof tourStopOfflineStatusActions>
  | RootActionType<typeof tourContentActions>
  | RouterAction;
